import { Controller } from "@hotwired/stimulus"

// Voice-to-text input using Web Speech API
// Usage: Add data-controller="voice-input" to a container with a textarea
// The textarea should have data-voice-input-target="input"
// A mic button will be rendered if speech recognition is supported

export default class extends Controller {
  static targets = ["input", "button"]

  connect() {
    // Check for Web Speech API support
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SpeechRecognition) {
      this.element.classList.add("voice-unsupported")
      return
    }

    this.recognition = new SpeechRecognition()
    this.recognition.continuous = true
    this.recognition.interimResults = true
    this.recognition.lang = "en-US"

    this.isRecording = false
    this.finalTranscript = ""

    this.recognition.onresult = this.handleResult.bind(this)
    this.recognition.onerror = this.handleError.bind(this)
    this.recognition.onend = this.handleEnd.bind(this)

    // Show the button since speech is supported
    if (this.hasButtonTarget) {
      this.buttonTarget.classList.remove("hidden")
    }
  }

  disconnect() {
    if (this.recognition && this.isRecording) {
      this.recognition.stop()
    }
  }

  toggle() {
    if (this.isRecording) {
      this.stop()
    } else {
      this.start()
    }
  }

  start() {
    if (!this.recognition || this.isRecording) return

    // Store current text to append to
    this.startingText = this.inputTarget.value
    this.finalTranscript = ""

    try {
      this.recognition.start()
      this.isRecording = true
      this.updateButtonState()
    } catch (e) {
      console.error("Speech recognition error:", e)
    }
  }

  stop() {
    if (!this.recognition || !this.isRecording) return

    this.recognition.stop()
    this.isRecording = false
    this.updateButtonState()
  }

  handleResult(event) {
    let interimTranscript = ""

    for (let i = event.resultIndex; i < event.results.length; i++) {
      const transcript = event.results[i][0].transcript
      if (event.results[i].isFinal) {
        this.finalTranscript += transcript
      } else {
        interimTranscript += transcript
      }
    }

    // Update the textarea with current transcription
    const separator = this.startingText && !this.startingText.endsWith(" ") ? " " : ""
    this.inputTarget.value = this.startingText + separator + this.finalTranscript + interimTranscript

    // Trigger input event so other controllers can react
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  handleError(event) {
    console.error("Speech recognition error:", event.error)
    this.isRecording = false
    this.updateButtonState()

    if (event.error === "not-allowed") {
      if (window.showToast) {
        window.showToast("Microphone access denied. Please enable it in your browser settings.", "error")
      }
    }
  }

  handleEnd() {
    this.isRecording = false
    this.updateButtonState()
  }

  updateButtonState() {
    if (!this.hasButtonTarget) return

    if (this.isRecording) {
      this.buttonTarget.classList.add("recording")
      this.buttonTarget.setAttribute("aria-pressed", "true")
      this.buttonTarget.title = "Stop recording"
    } else {
      this.buttonTarget.classList.remove("recording")
      this.buttonTarget.setAttribute("aria-pressed", "false")
      this.buttonTarget.title = "Start voice input"
    }
  }
}
